import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { useTranslation } from '@common/hooks/translation';
import { swarmRequest } from '@common/helpers/util/requestMiddleware';
import { PopupContext } from '@app/dev/contexts/popup';
import { POPUPS } from '@common/constants/popups';
import Config from '@app/dev/config/main';
import ToastWrapper from '@common/helpers/toastWrapper';
import NumericInput from '@common/components/numericInput';
import { getSwarmSetRfidPinCodeRequest } from '../../../helpers/terminalRequests/rfid';
import { Logout } from '../../../actions/login';

import './style.scss';

const SetPinCode = ({
  rfid, loggedIn, loginPin, openPopup,
}) => {
  const t = useTranslation();
  const [{ closePopup }] = useContext(PopupContext);
  const isAdminLoggedIn = useSelector((state) => state.terminal.isAdminLoggedIn);
  const isCardRegistered = useSelector((state) => state.terminal.isCardRegistered);
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    PinCode: '',
    ConfirmPinCode: '',
    Rfid: rfid,
  });

  const wrongConfirmPin = useMemo(
    () => form.PinCode && form.ConfirmPinCode && form.PinCode !== form.ConfirmPinCode,
    [form],
  );
  const isFormValid = useMemo(
    () => form.PinCode.length === 4
      && form.PinCode !== '0000'
      && form.ConfirmPinCode
      && !wrongConfirmPin
      && _.isInteger(+form.PinCode),
    [form, wrongConfirmPin],
  );

  const submitDisabled = useMemo(() => {
    if (loggedIn) return !isFormValid || !rfid;

    return !isFormValid || !isCardRegistered || (Config.main.needAdminForResetPin && !isAdminLoggedIn) || !rfid;
  }, [isFormValid, isAdminLoggedIn, isCardRegistered, rfid]);

  const fieldChange = (name, value) => {
    if (value.length <= 4) {
      setForm({
        ...form,
        [name]: value.trim(),
      });
    }
  };

  const submit = async () => {
    if (submitDisabled) return;

    const { ConfirmPinCode, ...params } = form;
    try {
      await swarmRequest(getSwarmSetRfidPinCodeRequest(params), 'betshop_command');

      closePopup();
      dispatch(Logout());
      openPopup(POPUPS.RESET_SUCCESS, { title: 'PIN code was succeddfully changed' });
    } catch (ex) {
      ToastWrapper('error', t('Something went wrong'));
    }
  };

  const handleCancel = () => {
    if (loginPin === '0000') dispatch(Logout());
    closePopup();
  };

  return (
    <div className="popup-content popup-small">
      <div className="popup-title">{t('Change PIN Code')}</div>
      <div className="popup-main-content">
        {Config.main.needAdminForResetPin && !isAdminLoggedIn && (
          <p className="text-center">{t('Please ask administrator for confirmation')}</p>
        )}

        <div className="input-wrapper">
          <div className="popup-txt">{t('Set new PIN Code')}</div>
          <div className="input-row">
            <NumericInput
              type="password"
              className="numeric-input"
              value={form.PinCode}
              onChange={(value) => fieldChange('PinCode', value)}
            />
          </div>
          <div className="popup-txt">{t('Repeat new PIN Code')}</div>
          <div className="input-row">
            <NumericInput
              type="password"
              className="numeric-input"
              value={form.ConfirmPinCode}
              onChange={(value) => fieldChange('ConfirmPinCode', value)}
            />
          </div>

        </div>
        {form.PinCode === '0000' && (
          <div className="error-row" style={{ display: '' }}>
            <i className="global-icon icon-toast-error error-icon-cont" />
            <p className="errorMessage">
              {t('PIN code cannot be only')}
              {' '}
              0000
            </p>
          </div>
        )}
        {form.PinCode === '0000' && (
          <div className="error-row" style={{ display: '' }}>
            <i className="global-icon icon-toast-error error-icon-cont" />
            <p className="errorMessage">
              {t('PIN code cannot be only')}
              {' '}
              0000
            </p>
          </div>
        )}
        {wrongConfirmPin && (
          <div className="error-row" style={{ display: '' }}>
            <i className="global-icon icon-toast-error error-icon-cont" />
            <div className="errorMessage">{t("PINs don't match")}</div>
          </div>
        )}

      </div>
      <div className="popup-actions">
        <button
          type="button"
          onClick={handleCancel}
          className="popup-cancel-button"
        >
          {t('Cancel')}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitDisabled}
          className="popup-confirm-button"
        >
          {t('Submit')}
        </button>
      </div>
    </div>
  );
};

SetPinCode.propTypes = {
  data: PropTypes.object,
  terminalBalance: PropTypes.number,
  cardRegistered: PropTypes.bool,
};

export default SetPinCode;
